import { useEffect, useRef } from 'react';
import confetti from 'canvas-confetti';
import { GameModal } from './GameModal';
import { COLORS, LABELS } from '@/game/configs';
import { Trophy } from 'lucide-react';
interface VictoryModalProps {
  isOpen: boolean;
  time: number;
  isNewBestTime: boolean;
  onPlayAgain: () => void;
  onReturnToLobby: () => void;
}
export function VictoryModal({
  isOpen,
  time,
  isNewBestTime,
  onPlayAgain,
  onReturnToLobby,
}: VictoryModalProps) {
  const hasTriggeredConfetti = useRef(false);
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };
  useEffect(() => {
    if (isOpen && !hasTriggeredConfetti.current) {
      hasTriggeredConfetti.current = true;
      const colors = [COLORS.yellow, '#FFA500', '#FF6347', '#FFD700'];
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 },
        colors,
      });
      setTimeout(() => {
        confetti({
          particleCount: 50,
          angle: 60,
          spread: 55,
          origin: { x: 0 },
          colors,
        });
      }, 200);
      setTimeout(() => {
        confetti({
          particleCount: 50,
          angle: 120,
          spread: 55,
          origin: { x: 1 },
          colors,
        });
      }, 400);
    }
    if (!isOpen) {
      hasTriggeredConfetti.current = false;
    }
  }, [isOpen]);
  return (
    <GameModal isOpen={isOpen}>
      <div className="flex flex-col items-center text-center gap-4">
        <div
          className="w-16 h-16 rounded-full flex items-center justify-center"
          style={{
            backgroundColor: COLORS.yellow,
            boxShadow: '4px 4px 12px rgba(255, 217, 61, 0.4)',
          }}
        >
          <Trophy size={32} style={{ color: COLORS.dark }} />
        </div>
        <div className="flex flex-col gap-1">
          <h2
            className="text-2xl sm:text-3xl font-extrabold"
            style={{
              fontFamily: 'Nunito, system-ui, sans-serif',
              color: COLORS.dark,
            }}
          >
            {LABELS.congratulations}
          </h2>
          <p
            className="text-base"
            style={{
              fontFamily: 'Nunito, system-ui, sans-serif',
              color: COLORS.brown,
            }}
          >
            Completed in {formatTime(time)}
          </p>
        </div>
        {isNewBestTime && (
          <div
            className="px-4 py-2 rounded-full"
            style={{
              backgroundColor: COLORS.yellow,
            }}
          >
            <span
              className="text-sm font-bold"
              style={{
                fontFamily: 'Nunito, system-ui, sans-serif',
                color: COLORS.dark,
              }}
            >
              {LABELS.newBestTime}
            </span>
          </div>
        )}
        <div className="flex flex-col gap-3 w-full mt-4">
          <button
            onClick={onPlayAgain}
            className="w-full py-4 rounded-2xl font-bold text-lg transition-all duration-150 active:scale-98"
            style={{
              backgroundColor: COLORS.yellow,
              boxShadow: '4px 4px 8px rgba(139, 115, 85, 0.3)',
              fontFamily: 'Nunito, system-ui, sans-serif',
              color: COLORS.dark,
            }}
          >
            {LABELS.playAgain}
          </button>
          <button
            onClick={onReturnToLobby}
            className="w-full py-3 rounded-2xl font-semibold text-base transition-all duration-150 active:scale-98"
            style={{
              backgroundColor: COLORS.ivory,
              boxShadow: '4px 4px 8px rgba(139, 115, 85, 0.2)',
              fontFamily: 'Nunito, system-ui, sans-serif',
              color: COLORS.brown,
            }}
          >
            {LABELS.returnToLobby}
          </button>
        </div>
      </div>
    </GameModal>
  );
}